'use client';

import { c as _c } from "react/compiler-runtime";
import type { QueryClient } from '@tanstack/react-query';
import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useMemo, useSyncExternalStore } from 'react';
import { useContentDetailsData } from '@src/hooks/useContentDetailsData';
import { usePostWorkoutExperienceController } from '@src/hooks/usePostWorkoutExperience';
import { useLocale } from '@src/i18n/LocaleProvider';
import { FAVORITES_INFINITE_SLUG } from '@src/utils/favorites.constants';
import type { InfiniteCollectionQueryData } from '@src/utils/infiniteCollectionQuery.util';
import { COCOA_COLLECTION_QUERY_KEY_PREFIX, type ParentContent, type ParentContentTypes } from '@src/utils/query.util';
import { patchFavoriteInContentQueryState } from '@src/utils/query.util';
import { patchFavoriteInCollectionQueryState } from '@src/utils/query.util';
import { patchFavoriteInEntityQueryState } from '@src/utils/query.util';
import { patchFavoriteInInfiniteCollectionQueryState } from '@src/utils/query.util';
import { getContentDetailsQueryKey } from '@src/utils/query.util';
export type FavoriteStateParams = {
  entityId?: string;
  parentContent?: CocoApi.Program | CocoApi.Compilation | CocoApi.BODiBlock | null;
  snapshotFallback: boolean;
};

/**
 * Resolves canonical `isFavorited` for an entity from react-query state from Cocoa
 * Subscribes to the QueryCache so updates re-render without a stale snapshot.
 * @param entityId - The id of the entity to update the favorite state of.
 * @param snapshotFallback - The initial value or fallback if entity is not found in the query state.
 * @returns The current favorite state and a function to update it.
 */
export function useFavoriteState(t0) {
  const $ = _c(16);
  const {
    entityId,
    snapshotFallback
  } = t0;
  const {
    content: cdpParentContent
  } = useContentDetailsData();
  const {
    postWorkoutParentContent
  } = usePostWorkoutExperienceController();
  const parentContent = cdpParentContent ?? postWorkoutParentContent;
  const {
    locale
  } = useLocale();
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== entityId || $[1] !== queryClient) {
    t1 = onStoreChange => {
      if (!entityId) {
        return _temp;
      }
      return queryClient.getQueryCache().subscribe(onStoreChange);
    };
    $[0] = entityId;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  let t2;
  if ($[3] !== entityId || $[4] !== locale || $[5] !== parentContent || $[6] !== queryClient) {
    t2 = () => entityId && locale.code ? readFavoriteFromQueryCache(queryClient, entityId, parentContent, locale.code) : undefined;
    $[3] = entityId;
    $[4] = locale;
    $[5] = parentContent;
    $[6] = queryClient;
    $[7] = t2;
  } else {
    t2 = $[7];
  }
  const queryFavorite = useSyncExternalStore(t1, t2, _temp2);
  let t3;
  bb0: {
    if (!entityId) {
      t3 = snapshotFallback;
      break bb0;
    }
    if (queryFavorite !== undefined) {
      t3 = queryFavorite;
      break bb0;
    }
    t3 = snapshotFallback;
  }
  const isFavorited = t3;
  let t4;
  if ($[8] !== entityId || $[9] !== locale || $[10] !== parentContent || $[11] !== queryClient) {
    t4 = nextFavorited => {
      if (parentContent && locale) {
        queryClient.setQueryData(getContentDetailsQueryKey(parentContent._type as ParentContentTypes, parentContent.slug, locale.code), prev => patchFavoriteInContentQueryState(prev, entityId, nextFavorited));
      }
      queryClient.setQueriesData({
        queryKey: [COCOA_COLLECTION_QUERY_KEY_PREFIX]
      }, prev_0 => patchFavoriteInCollectionQueryState(prev_0 as CocoApi.CollectionModule, entityId, nextFavorited) ?? prev_0);
      queryClient.setQueriesData({
        queryKey: [FAVORITES_INFINITE_SLUG]
      }, prev_1 => patchFavoriteInInfiniteCollectionQueryState(prev_1, entityId, nextFavorited) ?? prev_1);
      queryClient.setQueriesData({
        queryKey: ["entity", entityId, locale.code]
      }, prev_2 => patchFavoriteInEntityQueryState(prev_2, nextFavorited) ?? prev_2);
    };
    $[8] = entityId;
    $[9] = locale;
    $[10] = parentContent;
    $[11] = queryClient;
    $[12] = t4;
  } else {
    t4 = $[12];
  }
  const updateIsFavorited = t4;
  let t5;
  if ($[13] !== isFavorited || $[14] !== updateIsFavorited) {
    t5 = {
      isFavorited,
      updateIsFavorited
    };
    $[13] = isFavorited;
    $[14] = updateIsFavorited;
    $[15] = t5;
  } else {
    t5 = $[15];
  }
  return t5;
}

/**
 * Reads favorite state from react-query, preferring the CDP parent content query when available.
 */
function _temp2() {}
function _temp() {}
function readFavoriteFromQueryCache(queryClient: QueryClient, entityId: string, parentContent: ParentContent | null | undefined, locale: string): boolean | undefined {
  if (parentContent) {
    const cached = queryClient.getQueryData<ParentContent | null>(getContentDetailsQueryKey(parentContent._type as ParentContentTypes, parentContent.slug, locale));

    // CDP hero favorites the parent program/block itself — use only CDP state so we
    // don't briefly read a stale match from collection/favorites caches after refresh.
    if (parentContent._id === entityId) {
      if (cached) {
        return cached.userData?.isFavorited ?? false;
      }
      return parentContent.userData?.isFavorited ?? false;
    }
    if (cached) {
      const nested = cached.entities?.[entityId];
      if (nested) {
        return nested.userData?.isFavorited ?? false;
      }
    }
  }
  for (const [, data] of queryClient.getQueriesData<CocoApi.CollectionModule>({
    queryKey: [COCOA_COLLECTION_QUERY_KEY_PREFIX]
  })) {
    const entity = data?.entities?.[entityId];
    if (entity) {
      return entity.userData?.isFavorited ?? false;
    }
  }
  for (const [, data] of queryClient.getQueriesData<InfiniteCollectionQueryData>({
    queryKey: [FAVORITES_INFINITE_SLUG]
  })) {
    for (const page of data?.pages ?? []) {
      const entity = page?.entities?.[entityId];
      if (entity) {
        return entity.userData?.isFavorited ?? false;
      }
    }
  }
  for (const [, entity] of queryClient.getQueriesData<CocoApi.Entity>({
    queryKey: ['entity']
  })) {
    if (entity?._id === entityId) {
      return entity.userData?.isFavorited ?? false;
    }
  }
  return undefined;
}